import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Package, Truck, CheckCircle, Eye, Clock, AlertTriangle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as categoryApi from '@/services/categoryApi';

const ShipmentRequestsSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  
  // View dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Confirm dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmRemarks, setConfirmRemarks] = useState('');
  const [confirming, setConfirming] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getShipmentRequests({ status: 'approved' });
      // Also fetch in_progress requests
      const inProgressData = await categoryApi.getShipmentRequests({ status: 'in_progress' });
      setRequests([...(data.requests || []), ...(inProgressData.requests || [])]);
    } catch (error) {
      toast.error('Failed to fetch shipment requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleViewRequest = async (requestId) => {
    try {
      const data = await categoryApi.getShipmentRequest(requestId);
      setSelectedRequest(data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to fetch request details');
    }
  };

  const openConfirmDialog = (request) => {
    setSelectedRequest(request);
    setConfirmRemarks('');
    setShowConfirmDialog(true);
  };

  const handleConfirmShipment = async () => {
    try {
      setConfirming(true);
      await categoryApi.confirmShipment(selectedRequest.request_id, confirmRemarks);
      toast.success('Shipment confirmed successfully!');
      setShowConfirmDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirming(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-500 text-white animate-pulse',
      high: 'bg-orange-500 text-white',
      normal: 'bg-blue-500 text-white',
      low: 'bg-gray-400 text-white'
    };
    return <Badge className={styles[priority] || styles.normal}>{priority?.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700 border-green-300',
      in_progress: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return <Badge className={`${styles[status] || ''} border`}>{status?.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getModuleName = (code) => {
    const names = {
      greige: 'Greige Section',
      greige_storage: 'Greige Storage',
      fob: 'FOB Section',
      goods_transfer: 'Goods Transfer'
    };
    return names[code] || code;
  };

  const stats = {
    urgent: requests.filter(r => r.priority === 'urgent').length,
    high: requests.filter(r => r.priority === 'high').length,
    pending: requests.length
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/incharge')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
              <p className="text-sm text-[#E8DCC8]">Approved requests for your section</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {stats.urgent > 0 && (
            <Card className="border-red-300 bg-red-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-600">Urgent Requests</p>
                    <p className="text-2xl font-bold text-red-700">{stats.urgent}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500 animate-pulse" />
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-orange-600">High Priority</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.high}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Pending</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.pending}</p>
                </div>
                <Truck className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Requests Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Truck className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Pending Shipments</h3>
              <p className="text-[#5A5F6E]">No approved shipment requests for your section</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Pending Shipments
                <Badge variant="outline" className="ml-2">{requests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total Qty</TableHead>
                      <TableHead>Approved By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests
                      .sort((a, b) => {
                        const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
                        return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
                      })
                      .map((request) => (
                        <TableRow 
                          key={request.request_id} 
                          className={`hover:bg-gray-50 ${request.priority === 'urgent' ? 'bg-red-50' : ''}`}
                        >
                          <TableCell className="font-mono text-sm font-medium">{request.request_id}</TableCell>
                          <TableCell>{getPriorityBadge(request.priority)}</TableCell>
                          <TableCell>{getModuleName(request.module)}</TableCell>
                          <TableCell>{request.items?.length || 0}</TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700 border-0">{request.total_shipment_qty}</Badge>
                          </TableCell>
                          <TableCell>{request.approved_by_name}</TableCell>
                          <TableCell>{getStatusBadge(request.status)}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewRequest(request.request_id)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openConfirmDialog(request)}
                                className="bg-green-600 hover:bg-green-700"
                                title="Confirm Shipment"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirm
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Request Details - {selectedRequest?.request_id}
                {selectedRequest?.priority && getPriorityBadge(selectedRequest.priority)}
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Module</p>
                    <p className="font-semibold">{getModuleName(selectedRequest.module)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approved By</p>
                    <p className="font-semibold">{selectedRequest.approved_by_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Qty</p>
                    <p className="font-semibold">{selectedRequest.total_shipment_qty}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approved On</p>
                    <p className="font-semibold">{new Date(selectedRequest.approved_at).toLocaleString()}</p>
                  </div>
                </div>
                
                {selectedRequest.approval_remarks && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">Manager Notes</p>
                    <p className="text-sm">{selectedRequest.approval_remarks}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-semibold mb-2">Items to Ship ({selectedRequest.items?.length || 0})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Barcode</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead>Ship Qty</TableHead>
                          <TableHead>Target FG</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRequest.items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                            <TableCell>{item.design_name}</TableCell>
                            <TableCell>{item.color || '-'}</TableCell>
                            <TableCell className="font-semibold">{item.shipment_qty}</TableCell>
                            <TableCell>{item.target_fg_name || item.design_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
              <Button 
                onClick={() => { setShowViewDialog(false); openConfirmDialog(selectedRequest); }}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Shipment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Shipment Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Confirm Shipment Completion
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Request: <span className="font-semibold">{selectedRequest?.request_id}</span></p>
                <p className="text-sm text-gray-600">Items: <span className="font-semibold">{selectedRequest?.items?.length || 0}</span></p>
                <p className="text-sm text-gray-600">Total Qty: <span className="font-semibold">{selectedRequest?.total_shipment_qty}</span></p>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700">
                  By confirming, you acknowledge that all items have been shipped as requested.
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Completion Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes about the shipment..."
                  value={confirmRemarks}
                  onChange={(e) => setConfirmRemarks(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleConfirmShipment} 
                disabled={confirming}
                className="bg-green-600 hover:bg-green-700"
              >
                {confirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming...</> : 'Confirm Completion'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ShipmentRequestsSection;
